
import Vue from "vue";
import TempStarsTableTitle from "@/components/shared/TempStarsTableTitle/TempStarsTableTitle.vue";
import BuildStatusPanel from "./BuildStatusPanel.vue";
import {
  triggerMobileBuild,
  getMobileBuildStatus,
  getRecentMobileBuild,
  cancelMobileBuild,
  getStoreLatestVersion,
  BuildPlatform,
  BuildEnvironment,
  MobileBuildDispatchResponse,
  MobileBuildStatusResponse
} from "@/api";
import {
  normalizeStoreMarketingVersion,
  cmpMarketingVersion
} from "@/utils/storeMarketingVersion";

interface PlatformState {
  environment: BuildEnvironment;
  marketingVersion: string;
  dispatching: boolean;
  polling: boolean;
  cancelling: boolean;
  run:
    | (MobileBuildDispatchResponse &
        Partial<MobileBuildStatusResponse>)
    | null;
  pollTimer: number | null;
}

function makeInitialState(): PlatformState {
  return {
    environment: "develop",
    marketingVersion: "",
    dispatching: false,
    polling: false,
    cancelling: false,
    run: null,
    pollTimer: null
  };
}

export default Vue.extend({
  name: "BuildTrigger",
  components: { TempStarsTableTitle, BuildStatusPanel },
  data: () => ({
    environments: [
      { text: "Develop", value: "develop" },
      { text: "Staging", value: "staging" },
      {
        text: "Pre-merge Production (staging code + prod config)",
        value: "pre-merge-production"
      },
      {
        text: "Post-merge Production (master code + prod config)",
        value: "production"
      }
    ],
    ios: makeInitialState(),
    android: makeInitialState(),
    storeVersions: {
      ios: null as string | null,
      android: null as string | null
    },
    storeVersionsLoading: {
      ios: true,
      android: true
    },
    snackbar: { visible: false, text: "", color: "success" },
    confirmDialog: {
      show: false,
      title: "",
      message: "",
      warning: "",
      confirmText: "OK",
      confirmColor: "primary" as string,
      icon: "" as string,
      iconColor: "primary" as string,
      resolve: null as null | ((value: boolean) => void)
    }
  }),
  computed: {
    iosBusy(): boolean {
      return this.ios.dispatching || this.ios.polling || this.ios.cancelling;
    },
    androidBusy(): boolean {
      return (
        this.android.dispatching ||
        this.android.polling ||
        this.android.cancelling
      );
    },
    iosVersionHint(): string {
      const v = normalizeStoreMarketingVersion(this.storeVersions.ios);
      if (v)
        return `Latest on App Store / TestFlight: ${v} — pick the same or higher.`;
      if (this.storeVersionsLoading.ios)
        return "Looking up latest App Store / TestFlight version…";
      return "Marketing version, e.g. 5.25.1.";
    },
    androidVersionHint(): string {
      const v = normalizeStoreMarketingVersion(this.storeVersions.android);
      if (v) return `Latest on Play Console: ${v} — pick the same or higher.`;
      if (this.storeVersionsLoading.android)
        return "Looking up latest Play Console version…";
      return "Marketing version, e.g. 5.23.1.";
    }
  },
  async mounted() {
    // Restore last known build state per platform from GitHub so refreshing the
    // page doesn't lose the running-build indicator.
    await Promise.all([
      this.restoreRecent("ios"),
      this.restoreRecent("android"),
      this.refreshStoreVersions()
    ]);
  },
  beforeDestroy() {
    this.stopPolling("ios");
    this.stopPolling("android");
  },
  methods: {
    showConfirm(opts: {
      title: string;
      message: string;
      warning?: string;
      confirmText?: string;
      confirmColor?: string;
      icon?: string;
      iconColor?: string;
    }): Promise<boolean> {
      return new Promise(resolve => {
        this.confirmDialog = {
          show: true,
          title: opts.title,
          message: opts.message,
          warning: opts.warning || "",
          confirmText: opts.confirmText || "Confirm",
          confirmColor: opts.confirmColor || "primary",
          icon: opts.icon || "",
          iconColor: opts.iconColor || "primary",
          resolve
        };
      });
    },
    resolveConfirm(value: boolean) {
      const r = this.confirmDialog.resolve;
      this.confirmDialog.show = false;
      this.confirmDialog.resolve = null;
      if (r) r(value);
    },
    versionError(platform: BuildPlatform): string {
      const state: PlatformState =
        platform === "ios" ? this.ios : this.android;
      const v = state.marketingVersion.trim();
      if (!v) return "";
      // Require canonical three-part semver-style versions (e.g. 5.25.0, 5.25.1).
      // App Store Connect normalizes CFBundleShortVersionString to x.y.z anyway,
      // so a two-part input like "5.25" gets displayed inconsistently next to
      // earlier "5.25.0" builds in TestFlight. Enforce the same shape on Android
      // for consistency.
      if (!/^\d+\.\d+\.\d+$/.test(v))
        return "Version must have three parts, e.g. 5.25.0 (not 5.25 or 5.25.0.1).";
      // Play Console release.name is often "30000233 (5.26.2)" — normalize to
      // marketing semver before comparing or the version code dominates.
      const latest = normalizeStoreMarketingVersion(
        this.storeVersions[platform]
      );
      if (latest && cmpMarketingVersion(v, latest) < 0) {
        const label =
          platform === "ios" ? "App Store / TestFlight" : "Play Console";
        return `Must be ${latest} or higher (latest on ${label}).`;
      }
      return "";
    },
    canTrigger(platform: BuildPlatform): boolean {
      const state: PlatformState =
        platform === "ios" ? this.ios : this.android;
      const v = state.marketingVersion.trim();
      return !!v && !this.versionError(platform);
    },
    cmpVersion(a: string, b: string): number {
      return cmpMarketingVersion(a, b);
    },
    async restoreRecent(platform: BuildPlatform) {
      const state: PlatformState =
        platform === "ios" ? this.ios : this.android;
      try {
        const { run } = await getRecentMobileBuild(platform);
        if (!run) return;
        // Hydrate state with the most recent run so the panel re-renders.
        state.run = {
          ...run,
          success: true,
          platform,
          environment: this.envFromBranch(run.headBranch),
          branch: run.headBranch || "",
          workflow:
            platform === "ios"
              ? "deploy-testflight.yml"
              : "deploy-play-internal.yml",
          runId: run.id
        };
        if (run.status !== "completed" && run.id) {
          this.startPolling(platform, run.id);
        }
      } catch (err) {
        // Non-fatal — page should still render even if GitHub call fails.
        // eslint-disable-next-line no-console
        console.warn(`restoreRecent(${platform}) failed:`, err);
      }
    },
    envFromBranch(branch?: string): BuildEnvironment {
      // staging branch can be either "staging" or "pre-merge-production"
      // (same code, different web config). We can't recover that distinction
      // from the branch name alone, so default to "staging" for display.
      // The actual run input is preserved on the run itself in GitHub.
      if (branch === "master") return "production";
      if (branch === "staging") return "staging";
      return "develop";
    },
    async cancel(platform: BuildPlatform) {
      const state: PlatformState =
        platform === "ios" ? this.ios : this.android;
      const run = state.run;
      if (!run || !run.runId) return;

      const summary =
        `${platform.toUpperCase()} build #${run.runId}\n` +
        `Environment: ${state.environment}\n` +
        `Branch: ${run.branch || run.headBranch}\n` +
        `Started: ${
          run.createdAt ? new Date(run.createdAt).toLocaleString() : "—"
        }`;

      const ok = await this.showConfirm({
        title: "Cancel build?",
        icon: "mdi-close-circle-outline",
        iconColor: "error",
        message:
          summary +
          "\n\nGitHub will stop the workflow run. The status panel will keep polling until the cancellation is confirmed.",
        confirmText: "Cancel build",
        confirmColor: "error"
      });
      if (!ok) return;

      state.cancelling = true;
      try {
        await cancelMobileBuild(run.runId);
        this.showSnackbar(
          `Cancellation requested for ${platform.toUpperCase()} build #${run.runId}. ` +
            "Polling until GitHub confirms…",
          "info"
        );
        // The existing poll will naturally observe the run transitioning to
        // status=completed, conclusion=cancelled; cancelling stays true
        // (showing the spinner on the button) until the run is no longer
        // in_progress / queued.
        if (state.pollTimer === null) {
          this.startPolling(platform, run.runId);
        }
      } catch (err) {
        const e = err as Error;
        this.showSnackbar(
          `Failed to cancel ${platform.toUpperCase()} build: ${e.message}`,
          "error"
        );
        state.cancelling = false;
      }
    },
    async refreshStoreVersions() {
      const fetchOne = async (platform: BuildPlatform) => {
        this.storeVersionsLoading[platform] = true;
        try {
          const { version } = await getStoreLatestVersion(platform);
          this.storeVersions[platform] = version;
        } catch (err) {
          // Non-fatal; the API may not have store credentials yet.
          // eslint-disable-next-line no-console
          console.warn(`Latest store version unavailable for ${platform}:`, err);
        } finally {
          this.storeVersionsLoading[platform] = false;
        }
      };
      await Promise.all([fetchOne("ios"), fetchOne("android")]);
    },
    async trigger(platform: BuildPlatform) {
      const state: PlatformState =
        platform === "ios" ? this.ios : this.android;
      if (!this.canTrigger(platform)) return;
      const marketingVersion = state.marketingVersion.trim();
      const platformLabel = platform === "ios" ? "iOS" : "Android";
      const envLabels: Record<BuildEnvironment, string> = {
        develop: "Develop",
        staging: "Staging",
        "pre-merge-production": "Pre-merge Production",
        production: "Post-merge Production"
      };
      const isProd =
        state.environment === "production" ||
        state.environment === "pre-merge-production";

      const ok = await this.showConfirm({
        title: `Trigger ${platformLabel} build?`,
        icon: platform === "ios" ? "mdi-apple" : "mdi-android",
        iconColor: platform === "ios" ? "primary" : "success",
        message:
          `Environment: ${envLabels[state.environment]}\n` +
          `Version: ${marketingVersion}\n` +
          `${platform === "ios" ? "Build number" : "Version code"}: auto\n\n` +
          "This will dispatch a GitHub Actions workflow.",
        warning: isProd
          ? "Production builds upload to TestFlight / Play Internal — testers will see a new version."
          : undefined,
        confirmText: `Trigger ${platformLabel} build`,
        confirmColor: platform === "ios" ? "primary" : "success"
      });
      if (!ok) return;

      this.stopPolling(platform);
      state.dispatching = true;
      state.run = null;

      try {
        const result = await triggerMobileBuild(
          platform,
          state.environment,
          false,
          marketingVersion
        );
        state.run = result;
        this.showSnackbar(
          `Dispatched ${platform.toUpperCase()} build (run #${result.runId ??
            "queued"}). Track progress on GitHub.`,
          "success"
        );
        if (result.runId) {
          this.startPolling(platform, result.runId);
        }
      } catch (error) {
        const err = error as Error;
        this.showSnackbar(
          `Failed to dispatch ${platform.toUpperCase()} build: ${err.message}`,
          "error"
        );
      } finally {
        state.dispatching = false;
      }
    },
    startPolling(platform: BuildPlatform, runId: number) {
      const state: PlatformState =
        platform === "ios" ? this.ios : this.android;
      state.polling = true;

      const tick = async () => {
        try {
          const status = await getMobileBuildStatus(runId);
          if (state.run) {
            state.run = { ...state.run, ...status };
          }
          if (status.status === "completed") {
            this.stopPolling(platform);
            state.cancelling = false;
            const ok = status.conclusion === "success";
            const cancelled = status.conclusion === "cancelled";
            this.showSnackbar(
              `${platform.toUpperCase()} build ${
                ok
                  ? "succeeded"
                  : cancelled
                  ? "was cancelled"
                  : `finished with conclusion: ${status.conclusion}`
              }`,
              ok ? "success" : cancelled ? "info" : "error"
            );
          }
        } catch (err) {
          // Keep polling — the GitHub API can briefly 5xx; we don't want to
          // abort just because of one transient error.
          // eslint-disable-next-line no-console
          console.warn("Poll error:", err);
        }
      };

      tick();
      state.pollTimer = window.setInterval(tick, 8000);
    },
    stopPolling(platform: BuildPlatform) {
      const state: PlatformState =
        platform === "ios" ? this.ios : this.android;
      if (state.pollTimer !== null) {
        window.clearInterval(state.pollTimer);
        state.pollTimer = null;
      }
      state.polling = false;
    },
    showSnackbar(text: string, color: "success" | "error" | "info" = "success") {
      this.snackbar = { visible: true, text, color };
    }
  }
});
